import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  Self,
  ViewChild,
} from '@angular/core';
import { DOCUMENT, NgClass } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, filter, merge, take } from 'rxjs';
import { getHours, getMinutes, setHours, setMinutes, startOfDay, startOfHour } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { AppFacade, AuthFacade } from '../../store';
import { Language } from '../../common/constants/language';
import { IconNames } from '../../common/constants/icon-names';
import { LOCALE_CODE_LIST } from '../../common/constants/date-config';
import {
  markAsTouchedAndUpdate,
  markRequiredControlWithAsteriskSymbol,
  toUtcTimestampFromLocalTime,
} from '../../common';
import { AutoUnsubscribe } from '../../common/decorators/auto-unsubscribe.decorator';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { DatepickerV2PickerType, DatepickerV2Preset } from './datepicker-v2.types';
import { createDatepickerV2Formats, DatepickerInternalContent, DatepickerV2DateAdapter } from './datepicker-v2.adapter';
import {
  applyPendingDateSelection,
  formatTimerDisplay,
  readMomentHourMinute,
  resizeTimerMenuPanel,
  syncDateDisplay,
} from './datepicker-v2.helpers';

@Component({
  selector: 'ui-datepicker-v2',
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    TranslateModule,
    SvgIconComponent,
  ],
  templateUrl: './datepicker-v2.component.html',
  styleUrls: ['./datepicker-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: DatepickerV2DateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useFactory: createDatepickerV2Formats },
  ],
})
@AutoUnsubscribe
export class DatepickerV2Component implements OnInit, ControlValueAccessor {
  @Input() label: string;
  @Input() min?: Date | string;
  @Input() max?: Date | string;
  @Input() disabled?: boolean;
  @Input() errorMessage?: string;
  @Input() fontIcon: string = IconNames.CalendarClock;
  @Input() testId?: string;
  @Input() pickerType: DatepickerV2PickerType = 'both';
  @Input() showWarningIcon: boolean;
  @Input() warningTooltip: string;
  @Input() timezoneText?: string;
  @Input() changeHourOnly = false;
  @Input() preset: DatepickerV2Preset = 'default';

  @Input() set timezone(value: string) {
    if (value) {
      this.startAt = toZonedTime(new Date(), value);
      (this.dateAdapter as DatepickerV2DateAdapter).setHubTimezone(value);
    }
  }

  @Input() set excludeDays(days: number[]) {
    if (days) {
      this.dateFilter = (d: Date | null): boolean => (d ? !days.includes(d.getDay()) : true);
    }
  }

  @Input() set isValidationTriggered(trigger: boolean) {
    if (trigger) {
      markAsTouchedAndUpdate(this.dateControl);
      this.cd.markForCheck();
    }
  }

  @ViewChild('dp') datepicker!: MatDatepicker<Date>;
  @ViewChild('timerMenuTrigger', { static: false }) timerMenuTrigger?: MatMenuTrigger;

  dateControl = new FormControl<Date>(null);
  hoursControl = new FormControl<number>(0);
  minutesControl = new FormControl<number>(0);

  startAt: Date;
  dateFilter: (date: Date | null) => boolean = (): boolean => true;
  iconNames = IconNames;
  subscriptions: Subscription[] = [];
  is24h = true;

  private _originalValue: Date | null = null;
  private _originalHours = 0;
  private _originalMinutes = 0;
  private _appliedPicker = false;
  private _hasTimerValue = false;
  private _pendingDateSub: Subscription | null = null;
  private _hourRawInput: string | null = null;
  private _minuteRawInput: string | null = null;

  isFieldRequired: (control: FormControl | AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  constructor(
    @Self() public ngControl: NgControl,
    private dateAdapter: DateAdapter<Date>,
    private appFacade: AppFacade,
    private authFacade: AuthFacade,
    private cd: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
  ) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    if (this.isHourOnlyMode) {
      this.startAt = startOfHour(new Date());
    }

    if (this.pickerType === 'calendar') {
      this.dateFormats.display.dateInput = { year: 'numeric', month: 'long', day: 'numeric' };
    }

    this.initTimeFormatSubscription();
    this.initLocaleSubscription();
    this.initValueEmission();
    this.initValidationSubscription();
  }

  writeValue(dateTime: Date | number | null): void {
    if (dateTime === null || dateTime === undefined) {
      this._hasTimerValue = false;
      this.dateControl.setValue(null, { emitEvent: false });
      this.hoursControl.setValue(0, { emitEvent: false });
      this.minutesControl.setValue(0, { emitEvent: false });
      this.cd.markForCheck();
      return;
    }
    this._hasTimerValue = true;

    if (this.isHourOnlyMode) {
      const hour = typeof dateTime === 'number' && dateTime < 24 ? dateTime : getHours(new Date(+dateTime));
      this.hoursControl.setValue(hour, { emitEvent: false });
      this.cd.markForCheck();
      return;
    }

    const d = new Date(+dateTime);
    this.startAt = d;
    this.dateControl.setValue(d, { emitEvent: false });
    // Use moment's own accessors when available — avoids browser-local offset skewing the display
    const momentHM = readMomentHourMinute(dateTime);
    this.hoursControl.setValue(momentHM?.hour ?? getHours(d), { emitEvent: false });
    this.minutesControl.setValue(momentHM?.minute ?? getMinutes(d), { emitEvent: false });
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.dateControl.disable({ emitEvent: false }) : this.dateControl.enable({ emitEvent: false });
    this.cd.markForCheck();
  }

  get isHourOnlyMode(): boolean {
    return this.pickerType === 'timer' && this.changeHourOnly;
  }

  get showDateField(): boolean {
    return this.pickerType !== 'timer';
  }

  get showTimeFields(): boolean {
    return this.pickerType !== 'calendar';
  }

  get hourDisplay(): string {
    if (this._hourRawInput !== null) {
      return this._hourRawInput;
    }
    return String(this.hoursControl.value ?? 0).padStart(2, '0');
  }

  get minuteDisplay(): string {
    if (this._minuteRawInput !== null) {
      return this._minuteRawInput;
    }
    return String(this.minutesControl.value ?? 0).padStart(2, '0');
  }

  get timerDisplay(): string {
    if (!this._hasTimerValue) {
      return '';
    }
    return formatTimerDisplay(this.hoursControl.value ?? 0, this.minutesControl.value ?? 0, this.is24h);
  }

  onTimerMenuOpened(): void {
    if (this.ngControl.control?.disabled) {
      this.timerMenuTrigger?.closeMenu();
      return;
    }
    if (!this._hasTimerValue) {
      const now = this.startAt ?? new Date();
      this.hoursControl.setValue(getHours(now), { emitEvent: false });
      this.minutesControl.setValue(getMinutes(now), { emitEvent: false });
      this.cd.markForCheck();
    }
    this._originalHours = this.hoursControl.value ?? 0;
    this._originalMinutes = this.minutesControl.value ?? 0;
    resizeTimerMenuPanel(this.document);
  }

  onTimerMenuApply(): void {
    this._hourRawInput = null;
    this._minuteRawInput = null;
    this._hasTimerValue = true;
    this.emitCombinedValue();
    this.timerMenuTrigger?.closeMenu();
    this.cd.markForCheck();
  }

  onTimerMenuCancel(): void {
    this._hourRawInput = null;
    this._minuteRawInput = null;
    this.hoursControl.setValue(this._originalHours, { emitEvent: false });
    this.minutesControl.setValue(this._originalMinutes, { emitEvent: false });
    this.timerMenuTrigger?.closeMenu();
    this.cd.markForCheck();
  }

  incrementHour(): void {
    const current = this.hoursControl.value ?? 0;
    this.hoursControl.setValue(current < 23 ? current + 1 : 0);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  decrementHour(): void {
    const current = this.hoursControl.value ?? 0;
    this.hoursControl.setValue(current > 0 ? current - 1 : 23);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  onHourInputChange(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this._hourRawInput = raw;
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 23) {
      this.hoursControl.setValue(parsed);
      this.syncDateControlDisplay();
    }
  }

  onHourBlur(_event: FocusEvent): void {
    this._hourRawInput = null;
    const clamped = Math.min(23, Math.max(0, this.hoursControl.value ?? 0));
    this.hoursControl.setValue(clamped);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  incrementMinute(): void {
    const current = this.minutesControl.value ?? 0;
    this.minutesControl.setValue(current < 59 ? current + 1 : 0);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  decrementMinute(): void {
    const current = this.minutesControl.value ?? 0;
    this.minutesControl.setValue(current > 0 ? current - 1 : 59);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  onMinuteInputChange(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this._minuteRawInput = raw;
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 59) {
      this.minutesControl.setValue(parsed);
      this.syncDateControlDisplay();
    }
  }

  onMinuteBlur(_event: FocusEvent): void {
    this._minuteRawInput = null;
    const clamped = Math.min(59, Math.max(0, this.minutesControl.value ?? 0));
    this.minutesControl.setValue(clamped);
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  onPickerOpened(): void {
    this._originalValue = this.dateControl.value;
    this._originalHours = this.hoursControl.value ?? 0;
    this._originalMinutes = this.minutesControl.value ?? 0;
    this._appliedPicker = false;
    this._pendingDateSub?.unsubscribe();
    this._pendingDateSub = null;
    const contentInstance = (this.datepicker as unknown as { _componentRef?: { instance?: DatepickerInternalContent } })
      ._componentRef?.instance;

    if (this.pickerType === 'both' && contentInstance?._model?.selectionChanged) {
      this._pendingDateSub = contentInstance._model.selectionChanged.subscribe(
        ({ selection }: { selection: unknown }) => {
          if (selection instanceof Date) {
            this.onPendingDateSelected(selection);
          }
        },
      );
    }

    if (!this._originalValue && this.pickerType !== 'timer' && contentInstance) {
      const now = this.startAt ?? new Date();
      const h = getHours(now);
      const m = getMinutes(now);

      if (this.pickerType === 'both') {
        this.hoursControl.setValue(h, { emitEvent: false });
        this.minutesControl.setValue(m, { emitEvent: false });
        contentInstance._model?.updateSelection(now, this);
      } else {
        this.dateControl.setValue(startOfDay(now), { emitEvent: false });
      }

      contentInstance._changeDetectorRef?.markForCheck();
      this.cd.markForCheck();
    }
  }

  onPickerApply(): void {
    this._appliedPicker = true;
    this.emitCombinedValue();
  }

  onPickerClosed(): void {
    this._pendingDateSub?.unsubscribe();
    this._pendingDateSub = null;

    if (!this._appliedPicker && this.pickerType !== 'calendar') {
      this.dateControl.setValue(this._originalValue, { emitEvent: false });
      this.hoursControl.setValue(this._originalHours, { emitEvent: false });
      this.minutesControl.setValue(this._originalMinutes, { emitEvent: false });
    }
    this._appliedPicker = false;

    setTimeout(() => {
      this.syncDateControlDisplay();
      this.cd.markForCheck();
    }, 0);
  }

  onDateInputFocus(): void {
    if (this.pickerType !== 'both') {
      return;
    }
    this.syncDateControlDisplay();
    this.cd.markForCheck();
  }

  private onPendingDateSelected(date: Date): void {
    applyPendingDateSelection(date, this.hoursControl, this.minutesControl, this.dateControl);
    this.cd.markForCheck();
  }

  private syncDateControlDisplay(): void {
    if (!this.dateControl.value || this.pickerType !== 'both') {
      return;
    }
    syncDateDisplay(this.dateControl, this.hoursControl, this.minutesControl);
  }

  private onChange: (value: number) => void;
  private onTouched: () => void;

  private initTimeFormatSubscription(): void {
    const sub = this.authFacade.isTwentyFourHourTime$.pipe(take(1)).subscribe((is24h: boolean) => {
      this.is24h = is24h;
      (this.dateAdapter as DatepickerV2DateAdapter).setIs24h(is24h);
      this.syncDateControlDisplay();
      this.cd.markForCheck();
    });
    this.subscriptions.push(sub);
  }

  private initLocaleSubscription(): void {
    const sub = this.appFacade.currentLanguage$.pipe(filter(Boolean)).subscribe((language: Language) => {
      this.dateAdapter.setLocale(LOCALE_CODE_LIST[language]);
    });
    this.subscriptions.push(sub);
  }

  private initValueEmission(): void {
    const controls = this.getControlsToWatch();
    if (controls.length === 0) {
      return;
    }
    const sub = merge(...controls.map((c: FormControl) => c.valueChanges)).subscribe(() => {
      this.emitCombinedValue();
    });
    this.subscriptions.push(sub);
  }

  private getControlsToWatch(): FormControl[] {
    if (this.isHourOnlyMode) {
      return [this.hoursControl];
    }
    if (this.pickerType === 'both' || this.pickerType === 'calendar') {
      return [this.dateControl];
    }
    return [];
  }

  private emitCombinedValue(): void {
    if (this.isHourOnlyMode) {
      const h = this.hoursControl.value;
      if (h !== null && h !== undefined) {
        this.onChange(h);
      }
      return;
    }
    if (this.pickerType === 'calendar') {
      const date = this.dateControl.value;
      if (date) {
        this.onChange(toUtcTimestampFromLocalTime(date, 0, 0));
      }
      return;
    }
    const baseDate = this.pickerType === 'timer' ? this.startAt ?? new Date() : this.dateControl.value;
    if (!baseDate) {
      return;
    }
    const hours = this.hoursControl.value ?? 0;
    const minutes = this.minutesControl.value ?? 0;
    const combined = setMinutes(setHours(startOfDay(baseDate), hours), minutes);
    if (this.pickerType !== 'timer' && combined.getTime() !== (this.dateControl.value?.getTime() ?? -1)) {
      this.dateControl.setValue(combined, { emitEvent: false });
      this.cd.markForCheck();
    }
    this.onChange(toUtcTimestampFromLocalTime(baseDate, hours, minutes));
  }

  private initValidationSubscription(): void {
    if (!this.ngControl) return;
    this.dateControl.setValidators(this.ngControl.control?.validator);
    const sub = this.ngControl.statusChanges?.subscribe(() => this.cd.markForCheck());
    if (sub) this.subscriptions.push(sub);
  }
}
